import { useState, useEffect, useCallback, useRef } from 'react';
import { api, API_BASE_URL, tenantHeaders } from '../config/api';
import { EnhancedChatMessage, StudySession } from '../types/educational-chat';

interface MessageAttachment {
  type: 'image';
  data_url: string;
  file_name: string;
  mime_type: string;
}

interface UseGeneralChatReturn {
  messages: EnhancedChatMessage[];
  sessions: StudySession[];
  activeSessionId: string | null;
  isLoading: boolean;
  isStreaming: boolean;
  streamingMessageId: string | null;
  agentStatus: string;
  error: string | null;
  sendMessage: (text: string, booksContext?: string[], attachments?: MessageAttachment[]) => Promise<void>;
  createNewSession: () => void;
  selectSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  renameSession: (sessionId: string, newTitle: string) => Promise<void>;
  stopStreaming: () => void;
}

export const useGeneralChat = (initialSessionId?: string | null): UseGeneralChatReturn => {
  const [messages, setMessages] = useState<EnhancedChatMessage[]>([]);
  const [sessions, setSessions] = useState<StudySession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [agentStatus, setAgentStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Per-session message cache for background streaming support
  const [messagesCache, setMessagesCache] = useState<Record<string, EnhancedChatMessage[]>>({});
  // Track which session is currently streaming (may differ from activeSessionId)
  const [streamingSessionId, setStreamingSessionId] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  // Ref to track current active session for real-time comparison in stream handler
  const activeSessionIdRef = useRef<string | null>(null);
  // Ref to track messages cache for real-time access (avoids stale closure issues)
  const messagesCacheRef = useRef<Record<string, EnhancedChatMessage[]>>({});
  // Ref to store initial session ID from URL (avoids adding to dependencies)
  const initialSessionIdRef = useRef<string | null | undefined>(initialSessionId);
  // Ref to track current messages for selectSession (avoids stale closure)
  const messagesRef = useRef<EnhancedChatMessage[]>([]);

  // Keep activeSessionIdRef in sync with state for real-time access in stream handler
  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  // Keep messagesCacheRef in sync with state for real-time access
  useEffect(() => {
    messagesCacheRef.current = messagesCache;
  }, [messagesCache]);

  // Keep messagesRef in sync with state for selectSession
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSessions = async () => {
    try {
      // Fetch all general chat sessions using new unified endpoint
      const response = await api.get('/api/chat/sessions', {
        params: { session_type: 'general_chat' }
      });
      const generalSessions = response.data.sessions.map((s: any) => ({
        id: s.id,
        title: s.title || s.session_name,
        session_type: s.session_type,
        message_count: 0,
        last_activity: s.updated_at,
        created_at: s.created_at
      }));
      setSessions(generalSessions);

      // Priority for selecting initial session:
      // 1) URL param (initialSessionId from ref)
      // 2) API active_session_id
      // 3) Most recent session
      let targetSessionId: string | null = null;
      const urlSessionId = initialSessionIdRef.current;

      if (urlSessionId && generalSessions.some((s: StudySession) => s.id === urlSessionId)) {
        // URL param takes priority - allows "Return to Chat" to work
        targetSessionId = urlSessionId;
      } else if (response.data.active_session_id) {
        targetSessionId = response.data.active_session_id;
      } else if (generalSessions.length > 0) {
        targetSessionId = generalSessions[0].id;
      }

      if (targetSessionId) {
        setActiveSessionId(targetSessionId);
        await loadSessionMessages(targetSessionId);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      // Sessions might not exist yet, which is fine
    }
  };

  const loadSessionMessages = async (sessionId: string, useCache: boolean = true) => {
    try {
      // Check cache first (for quick session switching during streaming)
      // Use ref to get latest cache value (avoids stale closure issues)
      if (useCache && messagesCacheRef.current[sessionId]) {
        setMessages(messagesCacheRef.current[sessionId]);
        return;
      }

      setIsLoading(true);
      setError(null);
      const response = await api.get(`/api/chat/sessions/${sessionId}/messages`);
      const enhancedMessages = response.data.map((msg: any) => ({
        id: msg.id,
        session_id: msg.session_id,
        message_type: msg.message_type,
        content_text: msg.content_text,
        citations: msg.citations || [],
        attachments: msg.attachments || [],
        created_at: msg.created_at
      }));
      setMessages(enhancedMessages);

      // Cache the loaded messages
      setMessagesCache(prev => ({
        ...prev,
        [sessionId]: enhancedMessages
      }));
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  };

  const createNewSession = useCallback(() => {
    // Clear current session to start fresh
    setMessages([]);
    setActiveSessionId(null);
    setError(null);
    setAgentStatus('');
  }, []);

  const selectSession = useCallback(async (sessionId: string) => {
    // Use refs to access current state without adding dependencies
    // This prevents the callback from being recreated on every state change
    const currentActiveId = activeSessionIdRef.current;
    const currentMessages = messagesRef.current;

    // Save current session's messages to cache before switching
    if (currentActiveId && currentMessages.length > 0) {
      setMessagesCache(prev => ({
        ...prev,
        [currentActiveId]: currentMessages
      }));
    }

    setActiveSessionId(sessionId);
    setError(null);
    await loadSessionMessages(sessionId);
  }, []); // Empty deps - stable callback using refs

  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await api.delete(`/api/chat/sessions/${sessionId}`);

      // Remove from local state
      setSessions(prev => prev.filter(s => s.id !== sessionId));

      // If deleted session was active, clear messages
      if (sessionId === activeSessionId) {
        setMessages([]);
        setActiveSessionId(null);
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete session');
    }
  }, [activeSessionId]);

  const renameSession = useCallback(async (sessionId: string, newTitle: string) => {
    try {
      await api.patch(`/api/chat/sessions/${sessionId}`, {
        session_name: newTitle
      });

      // Update local state
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, title: newTitle } : s
      ));
    } catch (err) {
      console.error('Failed to rename session:', err);
      setError('Failed to rename session');
    }
  }, []);

  const sendMessage = useCallback(async (text: string, booksContext?: string[], attachments?: MessageAttachment[]) => {
    if (!text.trim() || isStreaming) return;

    try {
      setIsLoading(true);
      setIsStreaming(true);
      setError(null);
      setAgentStatus('');

      let sessionId: string = activeSessionId ?? '';

      // Create new session if needed
      if (!activeSessionId) {
        const sessionResponse = await api.post('/api/chat/sessions', {
          session_name: 'New Chat',
          session_type: 'general_chat'
        });
        sessionId = sessionResponse.data.id;
        setActiveSessionId(sessionId);

        // Add optimistic session
        const newSession: StudySession = {
          id: sessionId,
          title: undefined,
          session_type: 'general_chat',
          message_count: 0,
          last_activity: new Date().toISOString(),
          created_at: new Date().toISOString()
        };
        setSessions(prev => [newSession, ...prev]);
      }

      // Ensure sessionId is valid
      if (!sessionId) {
        throw new Error('Failed to create or get session ID');
      }

      // Track which session is streaming (for background streaming support)
      setStreamingSessionId(sessionId);

      // Add user message optimistically
      const userMessage: EnhancedChatMessage = {
        id: `temp-${Date.now()}`,
        session_id: sessionId,
        message_type: 'user',
        content_text: text,
        attachments: attachments,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);

      // Prepare AI message placeholder
      let aiMessageId = `temp-${Date.now() + 1}`;  // Use 'let' so we can update it when backend sends real ID
      const aiMessage: EnhancedChatMessage = {
        id: aiMessageId,
        session_id: sessionId,
        message_type: 'ai',
        content_text: '',
        citations: [],
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
      setStreamingMessageId(aiMessageId);

      // Start streaming with abort controller
      abortControllerRef.current = new AbortController();

      const response = await fetch(`${API_BASE_URL}/api/agent-chat/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          ...tenantHeaders(),
        },
        body: JSON.stringify({
          query: text,
          session_id: sessionId,
          current_page: 1, // For general chat, page doesn't matter
          books_context: booksContext || [], // Optional book IDs for multi-book context
          images: attachments || [] // Optional image attachments (backend uses 'images' parameter)
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to get response');
      }
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      // Parse SSE stream
      let currentEvent = '';
      let firstTokenReceived = false;
      let errorOccurred = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          // SSE format: "event: type\ndata: {...}"
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
            console.log(`[GeneralChat] Event received: ${currentEvent}`);
            continue;
          }

          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log(`[GeneralChat] Data for ${currentEvent}:`, data);

              // Handle different event types (using strict event type checking like Dhyana)
              if (currentEvent === 'status') {
                // Update agent status (e.g., "🧠 Analyzing...", "📚 Searching...")
                setAgentStatus(data.message || '');
              } else if (currentEvent === 'content') {
                // Clear loading state on first content token
                // Note: isStreaming stays true until streaming completes in finally block
                // Don't clear agentStatus here - the streaming container is already hidden
                // by the !message.content_text condition, and status is cleared in done/finally
                if (!firstTokenReceived) {
                  setIsLoading(false);
                  firstTokenReceived = true;
                }

                // Content streaming - append text tokens
                // Check if user is still viewing the streaming session
                if (activeSessionIdRef.current === sessionId) {
                  // Update visible messages directly
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? { ...msg, content_text: msg.content_text + (data.text || '') }
                      : msg
                  ));
                } else {
                  // User switched to different session - update cache for background streaming
                  setMessagesCache(prev => {
                    const newCache = {
                      ...prev,
                      [sessionId]: (prev[sessionId] || []).map(msg =>
                        msg.id === aiMessageId
                          ? { ...msg, content_text: msg.content_text + (data.text || '') }
                          : msg
                      )
                    };
                    // Also update ref immediately for real-time access
                    messagesCacheRef.current = newCache;
                    return newCache;
                  });
                }
              } else if (currentEvent === 'title') {
                // Persist title to backend AND update local state (like Dhyana does)
                if (data.title && sessionId) {
                  // Persist to backend
                  api.patch(`/api/chat/sessions/${sessionId}`, { title: data.title })
                    .catch(err => console.error('Failed to persist title:', err));

                  // Update local state
                  setSessions(prev => prev.map(s =>
                    s.id === sessionId
                      ? { ...s, title: data.title }
                      : s
                  ));
                }
              } else if (currentEvent === 'user_message') {
                // Update user message ID from backend
                if (data.id) {
                  const realUserId = data.id;
                  if (activeSessionIdRef.current === sessionId) {
                    setMessages(prev => prev.map(msg =>
                      msg.id === userMessage.id
                        ? { ...msg, id: realUserId }
                        : msg
                    ));
                  } else {
                    setMessagesCache(prev => {
                      const newCache = {
                        ...prev,
                        [sessionId]: (prev[sessionId] || []).map(msg =>
                          msg.id === userMessage.id
                            ? { ...msg, id: realUserId }
                            : msg
                        )
                      };
                      messagesCacheRef.current = newCache;
                      return newCache;
                    });
                  }
                }
              } else if (currentEvent === 'ai_message') {
                // Update AI message ID from backend
                if (data.id) {
                  const oldId = aiMessageId;  // Store old temp ID
                  const realAiId = data.id;   // Get real ID from backend
                  aiMessageId = realAiId;     // Update the variable (critical for content events!)
                  setStreamingMessageId(realAiId);
                  if (activeSessionIdRef.current === sessionId) {
                    setMessages(prev => prev.map(msg =>
                      msg.id === oldId  // Find message using OLD ID
                        ? { ...msg, id: realAiId }  // Update to REAL ID
                        : msg
                    ));
                  } else {
                    setMessagesCache(prev => {
                      const newCache = {
                        ...prev,
                        [sessionId]: (prev[sessionId] || []).map(msg =>
                          msg.id === oldId
                            ? { ...msg, id: realAiId }
                            : msg
                        )
                      };
                      messagesCacheRef.current = newCache;
                      return newCache;
                    });
                  }
                  console.log(`[GeneralChat] Updated AI message ID from ${oldId} to ${realAiId}`);
                }
              } else if (currentEvent === 'error') {
                // Error from backend - clear loading states and show error
                errorOccurred = true;
                setIsLoading(false);
                setIsStreaming(false);
                setAgentStatus('');

                // Ensure AI message exists to display error
                const errorMessage = data.message || 'An error occurred while processing your request.';
                setMessages(prev => {
                  const aiMsgExists = prev.find(m => m.id === aiMessageId);
                  if (!aiMsgExists) {
                    // Create AI message if it doesn't exist yet
                    return [...prev, {
                      id: aiMessageId,
                      session_id: sessionId,
                      message_type: 'ai',
                      content_text: errorMessage,
                      citations: [],
                      created_at: new Date().toISOString()
                    }];
                  }
                  // Update existing message
                  return prev.map(msg =>
                    msg.id === aiMessageId
                      ? { ...msg, content_text: errorMessage }
                      : msg
                  );
                });
                // Don't throw - let done event complete the stream
              } else if (currentEvent === 'done') {
                // Stream complete - refresh AI message from DB as safety net
                if (aiMessageId && !aiMessageId.startsWith('temp-')) {
                  try {
                    const response = await api.get(`/api/chat/messages/${aiMessageId}`);
                    const dbMessage = response.data;

                    if (activeSessionIdRef.current === sessionId) {
                      // Update visible messages with DB content
                      setMessages(prev => prev.map(msg =>
                        msg.id === aiMessageId
                          ? { ...msg, content_text: dbMessage.content_text, citations: dbMessage.citations || [] }
                          : msg
                      ));
                    } else {
                      // Update cache with DB content
                      setMessagesCache(prev => {
                        const newCache = {
                          ...prev,
                          [sessionId]: (prev[sessionId] || []).map(msg =>
                            msg.id === aiMessageId
                              ? { ...msg, content_text: dbMessage.content_text, citations: dbMessage.citations || [] }
                              : msg
                          )
                        };
                        messagesCacheRef.current = newCache;
                        return newCache;
                      });
                    }
                    console.log('[GeneralChat] Refreshed AI message from DB');
                  } catch (err) {
                    console.error('Failed to refresh message from DB:', err);
                    // Non-critical - streamed content is still there
                  }
                }
                break;
              }
            } catch (err) {
              console.error('Failed to parse SSE data:', err, line);
            }
            // Reset event after processing
            currentEvent = '';
          }
        }
      }

      // Update session last activity (only if no error occurred)
      if (!errorOccurred) {
        setSessions(prev => prev.map(s =>
          s.id === sessionId
            ? { ...s, last_activity: new Date().toISOString(), message_count: s.message_count + 2 }
            : s
        ));

        // Sync final messages to cache if user is still viewing this session
        // This ensures cache stays up to date for future session switches
        if (activeSessionIdRef.current === sessionId) {
          setMessages(currentMessages => {
            const newCache = {
              ...messagesCacheRef.current,
              [sessionId]: currentMessages
            };
            messagesCacheRef.current = newCache;
            setMessagesCache(newCache);
            return currentMessages; // Return unchanged
          });
        }
      }

    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Stream aborted by user');
        setAgentStatus('Stopped');
      } else {
        console.error('Failed to send message:', err);
        setError(err.message || 'Failed to send message. Please try again.');

        // Remove empty AI messages if no content received (check by message type and empty content)
        setMessages(prev => {
          // Find the last AI message with empty content (likely the failed one)
          const lastEmptyAiMsg = [...prev].reverse().find(m =>
            m.message_type === 'ai' && !m.content_text
          );
          if (lastEmptyAiMsg) {
            return prev.filter(m => m.id !== lastEmptyAiMsg.id);
          }
          return prev;
        });
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingMessageId(null);
      setStreamingSessionId(null);
      setAgentStatus('');
      abortControllerRef.current = null;
      readerRef.current = null;
    }
  }, [activeSessionId, isStreaming]);

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (readerRef.current) {
      readerRef.current.cancel();
      readerRef.current = null;
    }
    setIsStreaming(false);
    setStreamingMessageId(null);
    setStreamingSessionId(null);
    setAgentStatus('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, [stopStreaming]);

  return {
    messages,
    sessions,
    activeSessionId,
    isLoading,
    isStreaming,
    streamingMessageId,
    agentStatus,
    error,
    sendMessage,
    createNewSession,
    selectSession,
    deleteSession,
    renameSession,
    stopStreaming
  };
};

export default useGeneralChat;
